/**
 * Plan/billing API client - the user sees the active plan of every site they manage
 * plus its entitlements plus the platform plan catalog (for comparison). The scope is
 * decided by the server (user memberships); no site id is sent from here.
 */
import { api } from "./api";
import type { AddOnTargetInput, ChangeImpacts, PlanChangePreview, QuotaOptionSite } from "@wa/ui";

export type { PlanChangePreview };

export type EntitlementValue = boolean | number; // number: limit/quota, -1 = ∞
export type EntitlementMap = Record<string, EntitlementValue>;

export const UNLIMITED = -1;

export type EntitlementType = "boolean" | "limit" | "quota";
/**
 * Catalog category. Deliberately an open `string` and NOT a union: the source of truth is
 * the API (`apps/api/src/lib/entitlements.catalog.ts`), and every hand-written copy of that
 * union has drifted at least once. This file used to carry a five-member union that was
 * missing `builder`, which silently hid all fifteen of its rows from the account page.
 * Render order comes from `orderedCategories()` in @wa/ui, which appends unknown categories
 * instead of dropping them.
 */
export type EntitlementCategory = string;

export interface EntitlementDef {
  key: string;
  type: EntitlementType;
  category: EntitlementCategory;
  /** Exclusive-widget access tier (1 Effect / 2 Signature / 3 Flagship), served on
   *  `widget.*` keys only. Lets the UI roll fourteen widget rows into one line per tier. */
  tier?: 1 | 2 | 3;
}

export interface BillingPlan {
  code: string;
  name: Record<string, string>;
  description?: Record<string, string>;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  currency: string;
  features?: EntitlementMap;
  /** i18n bullet list ("what the plan includes") - the admin catalog is its source of truth. */
  highlights?: Record<string, string[]> | null;
  /** Emphasised card (e.g. "Most popular"). */
  featured?: boolean;
  /** i18n short label on the emphasised card; empty → falls back to `billing.popular`. */
  badge?: Record<string, string> | null;
  sortOrder?: number;
  /** Free trial (days); 0 = off. */
  trialDays?: number;
}

/** The account subscription's Stripe status (`GET /api/billing/status`, X-Site-Id scope). */
export interface BillingStatus {
  configured: boolean;
  planCode: string;
  status: string;
  billingCycle: "monthly" | "yearly";
  provider: string | null;
  hasStripeCustomer: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  trialUsed: boolean;
  refundEligible: boolean;
  refundDeadline: string | null;
}

/** Result of cancel/resume/refund. */
export interface BillingActionResult {
  ok: boolean;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  refundId?: string;
  amountCents?: number;
}

export interface BillingSite {
  siteId: string;
  siteName: string;
  /** The account (billing unit) the site belongs to. Sibling sites share one subscription. */
  accountId: string | null;
  /** Account site quota (used / allowed). `limit === -1` (UNLIMITED) → ∞. */
  quota: { used: number; limit: number } | null;
  planCode: string;
  status: string;
  /** The account subscription's billing cycle - lets the user switch their own plan's cycle in place. */
  billingCycle: "monthly" | "yearly";
  features: EntitlementMap;
  plan: BillingPlan | null;
}

export interface BillingOverview {
  sites: BillingSite[];
  plans: BillingPlan[];
  catalog: EntitlementDef[];
}

/** Entry of the public plan catalog (GET /api/public/plans, no authentication). */
export interface PublicPlan {
  code: string;
  productCode: string;
  name: Record<string, string>;
  description?: Record<string, string>;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  currency: string;
  featured?: boolean;
}

/**
 * Response of a checkout / plan change. `url` → Stripe Checkout (a new subscription).
 * `url:null` + `updated:true` → the plan was changed in place (prorated).
 */
export interface CheckoutResult {
  url: string | null;
  updated?: boolean;
  change?: "upgrade" | "downgrade" | "none";
  planCode?: string;
}

export const billing = {
  overview: () => api.get<BillingOverview>("/account/billing"),
  /** Public plan catalog (signup funnel: show the chosen plan before signing in). */
  publicPlans: () => api.get<PublicPlan[]>("/public/plans"),
  /**
   * Create a Stripe Checkout session for one site. `/billing/checkout` is site-scoped
   * → we pass `X-Site-Id` by hand (the account API does not add it otherwise).
   * `returnTo:"account"` → Stripe redirects back to the account billing tab.
   */
  checkout: (siteId: string, planCode: string, billingCycle: "monthly" | "yearly", addOns?: AddOnTargetInput[]) =>
    api.post<CheckoutResult>(
      "/billing/checkout",
      { planCode, billingCycle, returnTo: "account", ...(addOns?.length ? { addOns } : {}) },
      { headers: { "X-Site-Id": siteId } },
    ),
  /**
   * PREVIEW of the proration for a plan/cycle change - charges nothing, shown in the dialog.
   * Site-scoped like checkout → `X-Site-Id` by hand.
   */
  preview: (siteId: string, planCode: string, billingCycle: "monthly" | "yearly", addOns?: AddOnTargetInput[]) =>
    api.post<PlanChangePreview>(
      "/billing/preview",
      { planCode, billingCycle, ...(addOns?.length ? { addOns } : {}) },
      { headers: { "X-Site-Id": siteId } },
    ),
  /** The account subscription's Stripe status (cancellation/trial/refund eligibility). */
  status: (siteId: string) =>
    api.get<BillingStatus>("/billing/status", { headers: { "X-Site-Id": siteId } }),
  /** Cancel the subscription at period end (a "downgrade" to free = a cancellation). */
  cancel: (siteId: string) =>
    api.post<BillingActionResult>("/billing/cancel", undefined, { headers: { "X-Site-Id": siteId } }),
  /** Undo a scheduled cancellation (resume the subscription). */
  resume: (siteId: string) =>
    api.post<BillingActionResult>("/billing/resume", undefined, { headers: { "X-Site-Id": siteId } }),
  /** Self-service refund (30 day guarantee) - cancels immediately and refunds. */
  refund: (siteId: string) =>
    api.post<BillingActionResult>("/billing/refund", undefined, { headers: { "X-Site-Id": siteId } }),
  /** Self-service add-ons: list + prices + current quantities (from the account subscription). */
  addOns: (siteId: string) =>
    api.get<AddOnsOverview>("/billing/add-ons", { headers: { "X-Site-Id": siteId } }),
  /**
   * Add/change/remove an add-on (quantity=0 → remove). Prorated in Stripe.
   *
   * `keep` is REQUIRED when the decrease would push the account over its site quota (e.g.
   * dropping "+1 site" while the sites are still there) - without it the server answers
   * 409 `sites_over_quota` and returns the list of sites to choose from. That way the
   * enforcement cron does not decide for the customer which site gets suspended.
   */
  setAddOn: (siteId: string, addOnCode: string, quantity: number, keep?: string[]) =>
    api.post<AddOnSetResult>(
      "/billing/add-ons",
      { addOnCode, quantity, ...(keep ? { keep } : {}) },
      { headers: { "X-Site-Id": siteId } },
    ),
  /** IMPACT of an add-on change (limits dropping below usage + whether a site choice is required). */
  addOnPreview: (siteId: string, addOnCode: string, quantity: number) =>
    api.post<AddOnChangePreview>(
      "/billing/add-ons/preview",
      { addOnCode, quantity },
      { headers: { "X-Site-Id": siteId } },
    ),
};

// ── Add-ons (self-service extras) ─────────────────────────────────────────────

/** A self-service add-on's price + the account's current quantity. */
export interface BillingAddOn {
  code: string;
  name: Record<string, string>;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  currency: string;
  /** Whether a Stripe price exists for the current cycle (synced) → purchasable. */
  synced: boolean;
  /** Current quantity on the account subscription (0 = none). */
  quantity: number;
  /**
   * Largest allowed quantity. 1 = a switch-style extra (e.g. priority support) that applies
   * to the whole account - a second unit would add nothing but a double charge. Comes from
   * the server catalog (`addOnMaxQuantity`); the server enforces it, the UI only mirrors it.
   */
  maxQuantity?: number;
}

export interface AddOnsOverview {
  configured: boolean;
  /** The account has a live, modifiable Stripe subscription (the add-on attaches to it). */
  canPurchase: boolean;
  billingCycle: "monthly" | "yearly";
  currency: string;
  addOns: BillingAddOn[];
}

export interface AddOnSetResult {
  ok: boolean;
  change: "added" | "updated" | "removed" | "none";
  addOns: { addOnCode: string; quantity: number }[];
  /** How many sites the choice suspended (0 = no quota choice was needed). */
  sitesSuspended?: number;
}

/** `POST /api/billing/add-ons/preview` - "what do I lose?" before confirming. */
export interface AddOnChangePreview {
  impacts: ChangeImpacts;
  quota: { activeCount: number; allowed: number; deficit: number; over: boolean };
  /** The site quota would be exceeded → a choice is mandatory. */
  keepRequired: boolean;
  sites: QuotaOptionSite[];
}

/** An account site + its status (for the quota choice). */
export interface AccountSiteRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  /** Suspended specifically because of the quota (comes back on upgrade / on a new choice). */
  suspendedForQuota: boolean;
  host: string | null;
}

export interface AccountSitesOverview {
  accountId: string;
  isOwner: boolean;
  plan: string;
  quota: { used: number; limit: number };
  /** More active sites than the plan allows → the customer must choose which survive. */
  overQuota: boolean;
  quotaEnforceAt: string | null;
  sites: AccountSiteRow[];
}

/** Account site management (the quota choice). Site-scoped → `X-Site-Id`. */
export const accountSites = {
  overview: (siteId: string) =>
    api.get<AccountSitesOverview>("/account/sites", { headers: { "X-Site-Id": siteId } }),
  /** Choose the surviving sites (≤ quota); the remaining active ones are suspended. */
  keep: (siteId: string, keep: string[]) =>
    api.post<{ ok: boolean; kept: number; suspended: number }>(
      "/account/sites/keep",
      { keep },
      { headers: { "X-Site-Id": siteId } },
    ),
};
